import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import defaultImg from '../../asset/project/default_1.png';
import deleteIcon from '../../asset/icon/delete.png';
import { SelectedData } from '../../shared/dto/EntityRepository';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';

interface SelectedDataThumbProps {
  item: SelectedData;
  /** strip: 모바일 가로 줄. stack: 데스크톱 오른쪽 패널에서 세로로 쌓임 */
  layout?: 'strip' | 'stack';
}

const SelectedDataThumb: React.FC<SelectedDataThumbProps> = ({ item, layout = 'strip' }) => {
  const [isHover, setIsHover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { removeSelectedData } = useImportStationWorkStore();
  const imageUrl = useOptimizedImageUrl(item.originUrl ?? '', containerRef);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeSelectedData(item);
  };

  return (
    <ThumbWrapper
      $layout={layout}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <ImageBox ref={containerRef} $layout={layout}>
        <SelectedThumb
          $layout={layout}
          $isHover={isHover}
          src={imageUrl === '' ? defaultImg : imageUrl}
          alt=""
        />
        <HoverOverlay
          $isHover={isHover}
          onClick={handleRemove}
        >
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent={true}
            backgroundImg={deleteIcon}
            onClick={handleRemove}
            aria-label="Remove selected item"
          />
        </HoverOverlay>
      </ImageBox>
    </ThumbWrapper>
  );
};

const ThumbWrapper = styled.div<{ $layout: 'strip' | 'stack' }>`
  flex-shrink: 0;
  ${({ $layout }) =>
    $layout === 'stack'
      ? `
    width: 100%;
    display: flex;
    justify-content: flex-start;
  `
      : `
    height: 100%;
  `}
`;

const ImageBox = styled.div<{ $layout: 'strip' | 'stack' }>`
  position: relative;
  ${({ $layout }) =>
    $layout === 'stack'
      ? `
    max-width: 100%;
  `
      : `
    height: 100%;
  `}
`;

const SelectedThumb = styled.img<{ $isHover: boolean; $layout: 'strip' | 'stack' }>`
  object-fit: contain;
  display: block;
  ${({ $layout }) =>
    $layout === 'stack'
      ? `
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 200px;
  `
      : `
    height: 100%;
    width: auto;
  `}
  filter: ${({ $isHover }) => ($isHover ? 'blur(2px)' : 'none')};
  transition: filter 0.2s ease;
`;

const HoverOverlay = styled.div<{ $isHover: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isHover }) => ($isHover ? 1 : 0)};
  transition: opacity 0.2s ease;
  cursor: pointer;
`;

export default SelectedDataThumb;
